import {
  Alert,
  Box,
  Chip,
  LinearProgress,
  Stack,
  TextField,
} from "@mui/material";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

import { ACCEPTED_FILE_TYPES } from "../../../constants";
import { ChatInputWrapper } from "../../../views/assistant/components/ui/AssistantInputField";
import {
  useChatStore,
  useSupportsVision,
} from "../../../views/assistant/stores/ChatStore";
import { ImageContent } from "../../../views/assistant/types";
import { NucToast } from "../../components/Toast";
import { useFileUploadWithProgress } from "../../hooks/useFileUploadWithProgress";
import { FileUploadState } from "../../types/fileUploadWithProgress";
import type { NewConversation } from "../types/chatTypes";
import { useCreateDialogue } from "../ChatStreaming/services/api";
import {
  SUPPORTED_IMAGE_TYPES,
  routeImageFiles,
  useImagePaste,
} from "../hooks/useImagePaste";
import { useRichPaste } from "../hooks/useRichPaste";
import AttachmentsContainer from "./AttachmentsContainer";
import ChatInputAdorements from "./ChatInputAdorements";
import PastedImagesPreview from "./components/PastedImagesPreview";
import UploadFileButton from "./components/UploadFileButton";
import {
  TokenUsageGauge,
  type TokenUsageData,
  isContextExhausted,
  CONTEXT_WARNING_THRESHOLD,
} from "../TokenUsageGauge";
import { type QueuedMessage } from "../ChatStreaming/stores/CoreStreamingSlice";
import { CHAT_COLUMN_MAX_WIDTH } from "../StyledContainers/StyledContainers";

// Documents go to chunked upload; images are split out by routeImageFiles.
const CHAT_ACCEPTED_FILE_TYPES = [
  ...ACCEPTED_FILE_TYPES.EXTENSIONS.CHAT_PROJECT,
  ...SUPPORTED_IMAGE_TYPES,
];

const VISION_REQUIRED_MESSAGE =
  "Image uploads require a vision-capable model. Select an agent with the camera icon to enable image analysis.";

type ChatPromptInputProps = {
  value: string;
  isInputting: boolean;
  isChatLoading: boolean;
  isGenerating: boolean;
  InputBar?: React.FC<any>;
  InputButtons?: React.FC<any>;
  noFiles?: boolean;
  isSplashScreen?: boolean;
  existingFiles?: FileUploadState[]; // Files to restore (e.g., from splash screen navigation)
  /**
   * Target dialogue id for ad-hoc uploads, when the route can't supply it via
   * `useParams`. The projects chat route names the param `dialogue_id`, so the
   * `useParams<{ dialogueId }>()` lookup below misses it; passing the resolved
   * id here points uploads at the existing dialogue instead of minting one.
   */
  dialogueIdOverride?: number;
  placeholder?: string;
  product?: string | null;
  /**
   * Conversation payload for the create-on-first-attach mint, when the default
   * build below (ChatStore config + `product`) wouldn't produce the right
   * dialogue shape — e.g. sidebar product chats that must carry persona
   * datasets and product_context. Only consulted when no dialogue id is
   * resolvable yet.
   */
  uploadConversationSeed?: NewConversation;
  /**
   * Fired with the minted dialogue's id when a file attach created it. When
   * set, the host owns id adoption/URL sync, so the default
   * `history.replace` state hand-off is skipped.
   */
  onUploadDialogueCreated?: (dialogueId: number) => void;
  /**
   * Conversation identity for the upload-local state. When it changes, the
   * composer forgets the dialogue it minted on first attach, drops the upload
   * hook's runtime context id, and clears the attachment list — so a host that
   * resets its conversation in place (sidebar "New chat", subject switch)
   * doesn't keep uploading into the previous dialogue. Hosts must NOT derive
   * this from the dialogue id itself: the undefined → minted-id transition
   * would reset (and cancel) the very upload that minted it.
   */
  uploadResetKey?: string | number;
  /** Submit handler - receives pasted images for VLM support */
  onSubmit: (images?: ImageContent[]) => void;
  onStopChat: () => void;
  onChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onFilesChange?: (files: FileUploadState[]) => void;
  onRemoveFile?: (file: FileUploadState) => void;
  /** Token usage data for context window gauge */
  tokenUsage?: TokenUsageData | null;
  /** Callback to clear the input text */
  onClearInput?: () => void;
  /** Last user message text for Up arrow recall */
  lastUserMessage?: string;
  /** Callback to populate input with recalled message */
  onRecallMessage?: (text: string) => void;
  /** Queued messages waiting to be sent when current turn ends */
  stagedMessages?: QueuedMessage[];
  /** Pop the last staged message back into input for editing (Up arrow) */
  onEditLastStagedMessage?: () => void;
  /** Remove a specific staged message by index */
  onRemoveStagedMessage?: (index: number) => void;
  /** Override the default `aa-input-stop-btn` testid (e.g. for product-namespaced QA selectors). */
  stopBtnTestId?: string;
};

const ChatPromptInput = ({
  value,
  isInputting,
  isChatLoading,
  isGenerating,
  InputBar,
  InputButtons,
  noFiles,
  isSplashScreen,
  existingFiles = [],
  dialogueIdOverride,
  placeholder,
  product,
  uploadConversationSeed,
  onUploadDialogueCreated,
  uploadResetKey,
  onSubmit,
  onStopChat,
  onChange,
  onFilesChange,
  onRemoveFile,
  tokenUsage,
  onClearInput,
  lastUserMessage,
  onRecallMessage,
  stagedMessages = [],
  onEditLastStagedMessage,
  onRemoveStagedMessage,
  stopBtnTestId,
}: ChatPromptInputProps) => {
  // Check if current model supports vision/image inputs
  const supportsVision = useSupportsVision();

  // Image attachment hook for VLM support - images passed to onSubmit, no sync
  // callback. Paste goes through handlePaste; picker and drag-and-drop images
  // arrive via interceptFiles below. When supportsVision is false, pasting
  // shows an error message.
  const {
    images: pastedImages,
    addImages,
    handlePaste: handleImagePaste,
    removeImage,
    clearImages,
    error: imageError,
    pendingCount: pendingImageCount,
  } = useImagePaste({
    maxImages: 10,
    enabled: supportsVision,
    disabledMessage: VISION_REQUIRED_MESSAGE,
  });

  // Ref for the input field - used to anchor the paste format picker
  const inputRef = useRef<HTMLDivElement>(null);

  // Rich paste hook for tabular content (Excel, etc.)
  // Inserts converted text at cursor position or appends to value
  const handleRichPasteInsert = useCallback(
    (text: string) => {
      // Create a synthetic event to trigger onChange with new value
      const newValue = value ? `${value}\n${text}` : text;
      const syntheticEvent = {
        target: { value: newValue },
      } as React.ChangeEvent<HTMLTextAreaElement>;
      onChange(syntheticEvent);
    },
    [value, onChange],
  );

  const { handlePaste: handleRichPaste } = useRichPaste({
    onInsert: handleRichPasteInsert,
    autoConvert: true, // Auto-paste as HTML without showing picker
    defaultFormat: "html",
    onError: (message) => {
      NucToast({
        message,
        type: "error",
        hideProgressBar: false,
      });
    },
  });

  // Combined paste handler - check for images first, then tabular content
  // Images take priority because many sources include HTML metadata alongside image data
  const handlePaste = useCallback(
    (event: React.ClipboardEvent) => {
      const clipboardData = event.clipboardData;
      if (!clipboardData) return;

      // Check for images FIRST - images take priority over HTML
      // (Many image copies include HTML wrappers like <img> tags)
      const items = clipboardData.items;
      if (items) {
        for (let i = 0; i < items.length; i++) {
          if (items[i].type.startsWith("image/")) {
            handleImagePaste(event);
            return;
          }
        }
      }

      // No images - check for tabular content (HTML tables or TSV)
      const html = clipboardData.getData("text/html");
      const plainText = clipboardData.getData("text/plain");

      // Only intercept for actual table content, not just any HTML
      const hasHtmlTable = html && html.includes("<table");
      const hasTabSeparated =
        plainText &&
        plainText.includes("\t") &&
        plainText.split("\n").filter((l) => l.trim()).length >= 1;

      if (hasHtmlTable || hasTabSeparated) {
        // Use rich paste handler for tabular content
        handleRichPaste(event);
        return;
      }

      // Let default paste behavior handle plain text
    },
    [handleImagePaste, handleRichPaste],
  );

  // Context window exhaustion check
  const contextExhausted = isContextExhausted(tokenUsage);
  const contextWarning =
    tokenUsage && tokenUsage.percentage_used >= CONTEXT_WARNING_THRESHOLD;

  // Images still being read are not attached yet, so sending must wait.
  const isProcessingImages = pendingImageCount > 0;

  // Calculate effective isInputting - include pasted images for VLM support
  // This allows sending an image without text (e.g., "What is this?")
  const effectiveIsInputting =
    !isProcessingImages && (isInputting || pastedImages.length > 0);

  const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(event);
  };

  const handleFormSubmit = (event: React.FormEvent | React.KeyboardEvent) => {
    event.preventDefault();
    if (contextExhausted || isProcessingImages) return;
    // Convert pasted images to ImageContent format and pass to onSubmit
    const imagesToSend: ImageContent[] | undefined =
      pastedImages.length > 0
        ? pastedImages.map((img) => ({
            data: img.data,
            media_type: img.mediaType,
            detail: "auto" as const,
          }))
        : undefined;
    onSubmit(imagesToSend);
    // Clear pasted images after submit
    clearImages();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    // Escape - tiered: edit last staged message → stop generation → clear input → blur
    if (event.key === "Escape") {
      event.preventDefault();
      if (stagedMessages.length > 0 && onEditLastStagedMessage) {
        onEditLastStagedMessage();
      } else if (isGenerating || isChatLoading) {
        onStopChat();
      } else if (value !== "" && onClearInput) {
        onClearInput();
      } else {
        (event.target as HTMLElement).blur();
      }
      return;
    }

    // Up arrow - pop last staged message for editing, or recall last sent message
    if (event.key === "ArrowUp" && value === "") {
      if (stagedMessages.length > 0 && onEditLastStagedMessage) {
        event.preventDefault();
        onEditLastStagedMessage();
        return;
      }
      if (lastUserMessage && onRecallMessage) {
        event.preventDefault();
        onRecallMessage(lastUserMessage);
        return;
      }
    }

    if (event.key !== "Enter" || event.shiftKey) {
      return;
    }

    event.preventDefault();

    // Allow submission with text OR pasted images (VLM support)
    if (
      !isChatLoading &&
      !contextExhausted &&
      (value !== "" || pastedImages.length > 0)
    ) {
      // Validation happens in handleFormSubmit, just call it
      handleFormSubmit(event);
    }
  };

  const isStartingChat = isSplashScreen && (isChatLoading || isGenerating);

  const stagedMessagesBanner =
    stagedMessages.length > 0 ? (
      <Box sx={{ mx: 1.25, mt: 0.75, mb: 0.5 }}>
        <Box
          sx={{
            fontSize: "0.7rem",
            color: "text.secondary",
            fontWeight: 500,
            mb: 0.5,
            px: 0.5,
          }}
        >
          <HourglassEmptyIcon
            sx={{ fontSize: 12, mr: 0.5, verticalAlign: "middle" }}
          />
          {stagedMessages.length} queued message
          {stagedMessages.length > 1 ? "s" : ""} — sends at the next handoff
          point
        </Box>
        <Stack spacing={0.5}>
          {stagedMessages.map((msg, idx) => (
            <Box
              key={idx}
              sx={{
                p: 1,
                borderRadius: 1,
                border: "1px dashed",
                borderColor: "primary.light",
                bgcolor: "action.hover",
              }}
            >
              <Stack direction="row" alignItems="flex-start" spacing={1}>
                <Box
                  sx={{
                    fontSize: "0.65rem",
                    color: "text.disabled",
                    fontWeight: 600,
                    mt: 0.25,
                    flexShrink: 0,
                    minWidth: 16,
                    textAlign: "center",
                  }}
                >
                  {idx + 1}
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: "0.85rem",
                    color: "text.primary",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    maxHeight: 60,
                    overflow: "auto",
                  }}
                >
                  {msg.text}
                </Box>
                <Chip
                  label="×"
                  size="small"
                  data-attr="assistant:chip:remove-staged-message"
                  onClick={() => onRemoveStagedMessage?.(idx)}
                  sx={{
                    height: 20,
                    minWidth: 20,
                    fontSize: "0.75rem",
                    cursor: "pointer",
                    flexShrink: 0,
                    "& .MuiChip-label": { px: 0.5 },
                  }}
                />
              </Stack>
            </Box>
          ))}
        </Stack>
      </Box>
    ) : null;

  // --- Hooks for file upload mode (must be called unconditionally per Rules of Hooks) ---
  const { dialogueId: dialogueIdParam } = useParams<{ dialogueId: string }>();
  const dialogueId = Number(dialogueIdParam) || undefined;

  // State for created dialogue ID (for ad hoc uploads)
  const [createdDialogueId, setCreatedDialogueId] = useState<number | null>(
    null,
  );
  const history = useHistory();

  // Get chat configuration from unified store
  const { config: chatConfig } = useChatStore();

  // Use the proper dialogue creation hook
  const { mutateAsync: createDialogue, isLoading: isCreatingDialogue } =
    useCreateDialogue();

  // Effective dialogue ID - explicit override (projects chat) wins, then URL
  // param, then a dialogue created on first upload.
  const effectiveDialogueId =
    dialogueIdOverride || dialogueId || createdDialogueId || undefined;

  // Picked or dropped images go to the inline image flow; the upload hook
  // gets only the documents.
  const interceptFiles = useCallback(
    (files: File[]) =>
      routeImageFiles(files, supportsVision, addImages, () =>
        NucToast({
          message: VISION_REQUIRED_MESSAGE,
          type: "warning",
          hideProgressBar: false,
        }),
      ),
    [supportsVision, addImages],
  );

  const {
    files,
    isDragActive,
    removeFile,
    getRootProps,
    getInputProps,
    openFileDialog,
    updateContextId,
    resetContextId,
    resetFiles,
    validationError,
  } = useFileUploadWithProgress({
    context: "dialogue",
    contextId: effectiveDialogueId || "temp", // Use temp placeholder until dialogue is created
    maxFiles: 20,
    maxFileSize: Infinity, // Remove file size limit for chat uploads
    acceptedFileTypes: CHAT_ACCEPTED_FILE_TYPES,
    interceptFiles,
    allowMultiple: true,
    uploadConcurrency: "sequential",
    autoStartUpload: true, // Let hook handle auto-upload after context ID is set
    showFileList: false, // We'll handle display with AttachmentsContainer
    showIndividualProgress: true,
    showOverallProgress: false,
    existingFiles, // Pass existing files from parent (for splash -> dialogue navigation)
    // Handle file selection with dialogue creation if needed
    onFilesSelected: async () => {
      // If no dialogue ID exists, create one first
      if (!effectiveDialogueId && !isCreatingDialogue) {
        try {
          // Build the conversation request matching the API expectations —
          // unless the host supplied a seed (sidebar product chats, where the
          // dialogue must carry persona datasets and product_context).
          const conversation = uploadConversationSeed ?? {
            model_id: chatConfig?.modelId ?? null, // null = backend uses GeneralLLM
            model_version_number: chatConfig?.modelVersionNum ?? null,
            model_inference_data: {
              infer_data: JSON.stringify([]), // Empty initial messages
              stream: false, // For file uploads, we don't need streaming
              text_gen_model_params: chatConfig?.chatControlMetadata || {},
            },
            dataset_id: null,
            activity_id: null,
            search_enhanced: false,
            search_datasets: chatConfig?.datasets?.map((d) => d.id) || [],
            n_search_results: chatConfig?.topSearchResults || 5,
            tool_calling: true,
            chat_config_id: chatConfig?.chatConfigId || null,
            product: product || "atom-assist",
          };

          const newDialogueId = await createDialogue({ conversation });
          setCreatedDialogueId(newDialogueId);
          if (onUploadDialogueCreated) {
            // The host stream adopts the id (and owns URL sync) — don't
            // leak splash-navigation state into its history entry.
            onUploadDialogueCreated(newDialogueId);
          } else {
            history.replace({
              ...history.location,
              state: { initialUploadDialogueId: newDialogueId },
            });
          }
          // Update the hook's context ID so it can start uploading
          updateContextId(newDialogueId);
        } catch (error) {
          NucToast({
            message:
              "Failed to create dialogue for file upload. Please try again.",
            type: "error",
            hideProgressBar: false,
          });
          return;
        }
      }
    },
    onFilesChange, // Hook will call this whenever files change (proper architecture)
    onRemoveFile, // Hook calls this when a file is removed via removeFile()
    onError: (file, error) => {
      // toast the error
      NucToast({
        message: `Error uploading ${file.file.name}: ${error}`,
        type: "error",
        hideProgressBar: false,
      });
    },
  });

  // Conversation reset (see `uploadResetKey`): wipe every piece of
  // upload-local state the host can't reach. Skips the mount so restored
  // `existingFiles` aren't discarded.
  const lastUploadResetKey = useRef(uploadResetKey);
  useEffect(() => {
    if (lastUploadResetKey.current === uploadResetKey) return;
    lastUploadResetKey.current = uploadResetKey;
    setCreatedDialogueId(null);
    resetContextId();
    resetFiles();
    // resetFiles/resetContextId are recreated every render (plain closures
    // in the hook); keying the effect on them would fire it each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadResetKey]);

  if (noFiles) {
    return (
      <Box
        component="form"
        id="chat-prompt-input"
        data-testid="aa-input-form"
        data-attr="assistant:form:chat-prompt"
        sx={{
          textAlign: "center",
          maxWidth: CHAT_COLUMN_MAX_WIDTH,
          display: "flex",
          flexDirection: "column",
          alignSelf: "center",
          width: "100%",
          position: "relative",
          opacity: isStartingChat ? 0.5 : 1,
          pointerEvents: isStartingChat ? "none" : "auto",
        }}
        onSubmit={handleFormSubmit}
      >
        <ChatInputWrapper
          id="chat-input-wrapper"
          data-testid="aa-input-wrapper"
          sx={{
            border: "none",
            backgroundColor: "transparent",
          }}
        >
          {isStartingChat && <LinearProgress sx={{ position: "relative" }} />}

          {contextExhausted && (
            <Alert severity="error" sx={{ mx: 1, mt: 1, mb: 0.5 }}>
              Context window is full. Please start a new chat to continue.
            </Alert>
          )}
          {contextWarning && !contextExhausted && (
            <Alert severity="warning" sx={{ mx: 1, mt: 1, mb: 0.5 }}>
              Context window is almost full (
              {Math.round(tokenUsage!.percentage_used)}%). Consider starting a
              new chat soon.
            </Alert>
          )}

          {stagedMessagesBanner}

          <TextField
            key="chat-input-field"
            type="submit"
            variant="outlined"
            id="chat-input-text-field"
            data-testid="aa-input-textarea"
            inputProps={{
              "data-testid": "aa-input-textarea-inner",
              "data-attr": "assistant:input:chat-message",
            }}
            ref={inputRef}
            size="small"
            fullWidth
            placeholder={
              contextExhausted
                ? "Context window full — start a new chat"
                : placeholder || "Ask AtomAssist anything..."
            }
            value={value || ""}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            multiline
            minRows={isSplashScreen ? 2 : 1}
            maxRows={isSplashScreen ? 8 : 6}
            disabled={isStartingChat || contextExhausted}
            onDragOver={(e) => {
              // Allow drop on text field
              e.preventDefault();
            }}
            sx={{
              position: "relative",
              zIndex: 0,
              "& .MuiOutlinedInput-root.Mui-disabled": {
                "& fieldset": {
                  border: "none",
                },
              },
            }}
          />

          {/* Pasted images preview for VLM */}
          <PastedImagesPreview
            images={pastedImages}
            onRemove={removeImage}
            error={imageError}
            pendingCount={pendingImageCount}
          />

          {!InputButtons && (
            <Stack
              flexDirection="row"
              width="100%"
              justifyContent="space-between"
              alignItems="center"
              sx={{ px: 1.25, mb: 0.5 }}
            >
              <Stack
                direction="row"
                alignItems="center"
                gap={2}
                sx={{ marginLeft: "auto" }}
              >
                {tokenUsage && (
                  <TokenUsageGauge tokenUsage={tokenUsage} variant="circular" />
                )}
                <ChatInputAdorements
                  isInputting={effectiveIsInputting}
                  isChatLoading={isChatLoading}
                  isGenerating={isGenerating}
                  handleFormSubmit={handleFormSubmit}
                  onStopChat={onStopChat}
                  stopBtnTestId={stopBtnTestId}
                />
              </Stack>
            </Stack>
          )}
          {InputButtons && (
            <Box sx={{ px: 1.25, mt: 0.5, mb: 0.5 }}>
              <InputButtons
                isInputting={isInputting}
                isChatLoading={isChatLoading}
                isGenerating={isGenerating}
                handleFormSubmit={handleFormSubmit}
                onStopChat={onStopChat}
                tokenUsage={tokenUsage}
              />
            </Box>
          )}
        </ChatInputWrapper>
        {InputBar && (
          <Box
            sx={{
              bgcolor: "#FAFAF9",
              border: "1.5px solid #D9D9D0",
              borderRadius: "0px 0px 5px 5px",
              borderTop: "none",
              p: 1,
              width: "98%",
              display: "flex",
              flexDirection: "column",
              alignSelf: "center",
            }}
          >
            {
              <InputBar
                isInputting={isInputting}
                isChatLoading={isChatLoading}
                isGenerating={isGenerating}
                handleFormSubmit={handleFormSubmit}
                onStopChat={onStopChat}
              />
            }
          </Box>
        )}
      </Box>
    );
  }

  // Note: onFilesChange is now handled by the hook itself via its internal useEffect
  // No need for a separate useEffect here - the hook notifies us when files change

  return (
    <Box
      component="form"
      id="chat-prompt-input"
      data-testid="aa-input-form"
      data-attr="assistant:form:chat-prompt"
      sx={{
        textAlign: "center",
        maxWidth: CHAT_COLUMN_MAX_WIDTH,
        display: "flex",
        flexDirection: "column",
        alignSelf: "center",
        width: "100%",
        position: "relative",
        opacity: isStartingChat ? 0.5 : 1,
        pointerEvents: isStartingChat ? "none" : "auto",
      }}
      onSubmit={handleFormSubmit}
    >
      <ChatInputWrapper
        id="chat-input-wrapper"
        data-testid="aa-input-wrapper"
        sx={{
          border: isDragActive ? "2px dashed #1ACA6C" : "none",
          backgroundColor: isDragActive
            ? "rgba(26, 202, 108, 0.71)"
            : "transparent",
        }}
        {...getRootProps()}
      >
        {isStartingChat && <LinearProgress sx={{ position: "relative" }} />}
        <input
          {...getInputProps()}
          style={{ display: "none" }}
          data-testid="aa-input-file-hidden"
          data-attr="assistant:input:file-upload-hidden"
        />

        {contextExhausted && (
          <Alert severity="error" sx={{ mx: 1, mt: 1, mb: 0.5 }}>
            Context window is full. Please start a new chat to continue.
          </Alert>
        )}
        {contextWarning && !contextExhausted && (
          <Alert severity="warning" sx={{ mx: 1, mt: 1, mb: 0.5 }}>
            Context window is almost full (
            {Math.round(tokenUsage!.percentage_used)}%). Consider starting a new
            chat soon.
          </Alert>
        )}

        {stagedMessagesBanner}

        <TextField
          key="chat-input-field"
          type="submit"
          variant="outlined"
          id="chat-input-text-field"
          data-testid="aa-input-textarea"
          inputProps={{
            "data-testid": "aa-input-textarea-inner",
            "data-attr": "assistant:input:chat-message",
          }}
          ref={inputRef}
          size="small"
          fullWidth
          placeholder={
            contextExhausted
              ? "Context window full — start a new chat"
              : placeholder || "Type your message here..."
          }
          value={value || ""}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          multiline
          minRows={isSplashScreen ? 2 : 1}
          maxRows={isSplashScreen ? 8 : 6}
          disabled={isStartingChat || contextExhausted}
          onDragOver={(e) => {
            // Allow drop on text field
            e.preventDefault();
          }}
          sx={{
            position: "relative",
            zIndex: 0,
            "& .MuiOutlinedInput-root.Mui-disabled": {
              "& fieldset": {
                border: "none",
              },
            },
          }}
        />

        {/* Pasted images preview for VLM */}
        <PastedImagesPreview
          images={pastedImages}
          onRemove={removeImage}
          error={imageError}
          pendingCount={pendingImageCount}
        />

        {!InputButtons && (
          <Stack
            flexDirection="row"
            width="100%"
            justifyContent="space-between"
            alignItems="center"
            sx={{ px: 1.25, mb: 0.5 }}
          >
            <UploadFileButton openFileDialog={openFileDialog} />
            <Stack direction="row" alignItems="center" gap={2}>
              {tokenUsage && (
                <TokenUsageGauge tokenUsage={tokenUsage} variant="circular" />
              )}
              <ChatInputAdorements
                isInputting={isInputting}
                isChatLoading={isChatLoading}
                isGenerating={isGenerating}
                handleFormSubmit={handleFormSubmit}
                onStopChat={onStopChat}
                stopBtnTestId={stopBtnTestId}
              />
            </Stack>
          </Stack>
        )}
        {InputButtons && (
          <Box sx={{ px: 1.25, mt: 0.5, mb: 0.5 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              width="100%"
              gap={1}
            >
              <UploadFileButton openFileDialog={openFileDialog} />
              <InputButtons
                isInputting={effectiveIsInputting}
                isChatLoading={isChatLoading}
                isGenerating={isGenerating}
                handleFormSubmit={handleFormSubmit}
                onStopChat={onStopChat}
                tokenUsage={tokenUsage}
              />
            </Stack>
          </Box>
        )}

        {/* Attachments Container - Now below the chat input */}
        <AttachmentsContainer
          processedFiles={files}
          removeFile={(fileId: string) => {
            removeFile(fileId);
          }}
          isDragActive={isDragActive}
          validationError={validationError}
          uploadError={
            files.some(
              (f) =>
                f.uploadStatus === "failed" || f.processingStatus === "failed",
            )
              ? files.find(
                  (f) =>
                    f.uploadStatus === "failed" ||
                    f.processingStatus === "failed",
                )?.error || "Upload failed"
              : null
          }
        />
      </ChatInputWrapper>
      {InputBar && (
        <Box
          sx={{
            bgcolor: "#FAFAF9",
            border: "1.5px solid #D9D9D0",
            borderRadius: "0px 0px 5px 5px",
            borderTop: "none",
            p: 1,
            width: "98%",
            display: "flex",
            flexDirection: "column",
            alignSelf: "center",
          }}
        >
          {
            <InputBar
              isInputting={effectiveIsInputting}
              isChatLoading={isChatLoading}
              isGenerating={isGenerating}
              handleFormSubmit={handleFormSubmit}
              onStopChat={onStopChat}
            />
          }
        </Box>
      )}
    </Box>
  );
};

export default ChatPromptInput;
